import * as React from "react";
import { Eye, EyeOff } from "lucide-react";
import { cn } from "../utils/cn";

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  label?: string;
  isPasswordVisible?: boolean;
  onTogglePasswordVisibility?: () => void;
  onClick?: () => void;
  error?: string;
  helperText?: string;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type,
      leftIcon,
      rightIcon,
      label,
      isPasswordVisible,
      onTogglePasswordVisibility,
      onClick,
      error,
      helperText,
      ...props
    },
    ref,
  ) => {
    const hasLeftIcon = !!leftIcon;
    const hasRightIcon = !!rightIcon;
    const isPassword = type === "password";
    const showPasswordToggle = isPassword && onTogglePasswordVisibility;

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={props.id}
            className="block mb-2 text-sm font-medium text-juridico"
          >
            {label}
          </label>
        )}
        <div className={cn("mt-2", !label && "mt-0")}>
          <div
            className={cn(
              "border outline-none border-tiber-200 rounded-lg focus:ring-2 focus:border-transparent bg-romance-50 flex items-center",
              error
                ? "border-red-500 focus:ring-red-500"
                : "border-tiber-900 focus:ring-tiber-600",
              "transition-all duration-200",
              hasLeftIcon ? "pl-3" : "pl-4",
              !hasRightIcon && !showPasswordToggle && "pr-3",
            )}
          >
            {hasLeftIcon && (
              <div className="shrink-0 w-5 h-5 text-titan select-none flex items-center justify-center ml-2 mr-3 pointer-events-none">
                {leftIcon}
              </div>
            )}
            <input
              id={props.id}
              type={
                type === "password"
                  ? isPasswordVisible
                    ? "text"
                    : "password"
                  : type || "text"
              }
              name={props.name}
              placeholder={props.placeholder}
              className={cn(
                "block h-12 min-w-0 grow py-1.5 text-base text-tiber-900 w-full placeholder:text-titan focus:outline-none sm:text-sm/6",
                !hasLeftIcon && "pl-4",
                className,
              )}
              style={
                !hasLeftIcon && !className?.includes("pl-")
                  ? { paddingLeft: "1rem" }
                  : undefined
              }
              ref={ref}
              onClick={onClick}
              {...props}
            />
            {hasRightIcon && (
              <div className="shrink-0 w-5 h-5 text-titan select-none flex items-center justify-center ml-2 pointer-events-none">
                {rightIcon}
              </div>
            )}
            {showPasswordToggle && (
              <button
                type="button"
                onClick={onTogglePasswordVisibility}
                className="shrink-0 w-5 h-5 text-titan hover:text-tiber-700 flex items-center justify-center ml-2 mr-2 transition-colors focus:outline-none focus:ring-2 focus:ring-tiber-500 focus:ring-offset-1 rounded cursor-pointer"
                aria-label={
                  isPasswordVisible
                    ? "Ocultar contraseña"
                    : "Mostrar contraseña"
                }
              >
                {isPasswordVisible ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            )}
          </div>
          {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
          {helperText && !error && (
            <p className="mt-1 text-sm text-juridico/70">{helperText}</p>
          )}
        </div>
      </div>
    );
  },
);

Input.displayName = "Input";

export { Input };
